import { Autocomplete, Box, Divider, Drawer, Grid, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material"
import { FC, useEffect, useState } from "react"
import CloseIcon from "@mui/icons-material/Close";
import { Product } from "../../../types/slices/productType";
import { useStockProductStore } from "../../../hooks/useStockProductStore";
import CustomTable from "../../../components/common/CustomTable/CustomTable";
import { ApiStatus } from "../../../types/api/status";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Store } from "../../../types/slices/storeType";
import { useStoreStore } from "../../../hooks/useStoreStore";
import { formatDate, formatDateTime } from "../../../toolbox/helpers/formatDateHelper";
import { AdjustmentModal } from "./AdjustmentModal";
import EditIcon from "@mui/icons-material/Edit";

interface Props {
    open: boolean
    onClose: () => void
    product: Product
    getProductsApi: () => Promise<void>
}

interface onList {
    page?: number
    perPage?: number
    idwarehouse?: number
    idproduct?: number
    search?: string
    isAdjustment?: number
}

export const StockHistoryDrawer: FC<Props> = (props) => {
    const { open, onClose, product, getProductsApi } = props

    const { status: statusStock, stockproducts, totalRows, getStockProducts } = useStockProductStore()
    const { status: statusStore, store, getStore } = useStoreStore()

    const [page, setPage] = useState<number>(1)
    const [perPage, setPerPage] = useState<number>(10)

    const [selectedWarehouse, setSelectedWareHouse] = useState<Store | null>(null)
    const [stockStore, setStockStore] = useState<any>(undefined)
    const [showModal, setShowModal] = useState<boolean>(false)

    useEffect(() => {
        getStore()
    }, [])

    useEffect(() => {
        getDataAPI()
    }, [page, perPage, selectedWarehouse])

    const getDataAPI = async () => {
        const payload: onList = {
            page: page,
            perPage: perPage,
            idproduct: product?.id,
            idwarehouse: selectedWarehouse?.id
        }

        await getStockProducts(payload)
    }

    const handleChangePage = (event: any, newPage: any) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (event) => {
        setPerPage(+event.target.value)
        setPage(1)
    }

    const formatDifference = (difference: number) => {
        const absDifference = Math.abs(difference)

        return (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {difference > 0 ? (
                    <ArrowUpwardIcon color="success" fontSize="small" />
                ) : (
                    <ArrowDownwardIcon color="error" fontSize="small" />
                )}
                {absDifference}
            </span>
        )
    }

    const handleEdit = (row) => {
        setStockStore(row)
        setShowModal(true)
    }

    const columns = [
        { type: 'text', field: 'registration_date', label: 'Fecha', align: 'center', format: (row) => formatDateTime(row.registration_date) },
        { type: 'text', field: 'type_dte_name', label: 'Tipo', align: 'center', format: (row) => row.type_dte_name || '-' },
        { type: 'text', field: 'folio', label: 'Folio', align: 'center', format: (row) => row.folio || '-' },
        { type: 'text', field: 'store', label: 'Bodega', align: 'center' },
        { type: 'text', field: 'movement', label: 'Movimiento', align: 'center', },
        { type: 'text', field: 'initial_stock', label: 'Stock inicial', align: 'center', format: (row) => row.initial_stock ?? 0 },
        { type: 'text', field: 'difference', label: 'Variación', align: 'center', format: (row) => formatDifference(row.difference) },
        { type: 'text', field: 'stock_system', label: 'Stock Bodega', align: 'center' },
        { type: 'text', field: 'stock_total', label: 'Stock General', align: 'center' },
    ]

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Grid container sx={{ width: 1050, padding: 2 }} spacing={2}>
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        top: 8,
                        right: 8
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Grid item xs={12}>
                    <Typography variant="h6">
                        Historial de movimientos
                    </Typography>
                </Grid>
                <Grid item xs={12} mb={2}>
                    <Divider />
                </Grid>

                <Grid container xs={12} sx={{ backgroundColor: "#f5f5f5", borderRadius: 2, boxShadow: 2, padding: 2, ml: 2 }}>
                    <Grid item xs={6}>
                        <Box sx={{ padding: 2 }}>
                            <Typography variant="h6" fontWeight="bold" color="primary">
                                {product?.name || "Nombre del producto"}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 1 }}>
                                {product?.description || "Sin descripción"}
                            </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#37474F" }}>
                                Stock actual: {product?.total_stock || 0}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box sx={{ padding: 2 }}>
                            <TableContainer sx={{
                                maxHeight: 175,
                                overflowY: "auto",
                                borderRadius: 1,
                                border: "1px solid #ddd",
                                boxShadow: "0px 2px 4px rgba(0,0,0,0.1)"
                            }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                            <TableCell sx={{ fontWeight: "bold", color: "#616161" }}>Bodega</TableCell>
                                            <TableCell sx={{ fontWeight: "bold", color: "#616161" }}>Stock</TableCell>
                                            <TableCell sx={{ fontWeight: "bold", color: "#616161", textAlign: "center" }}>Acciones</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {product?.stockStore?.map((row, index) => (
                                            <TableRow key={index} hover>
                                                <TableCell>{row.name}</TableCell>
                                                <TableCell sx={{ fontWeight: "bold" }}>{row.stock}</TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="Realizar ajuste">
                                                        <IconButton size="small" color="primary" onClick={() => handleEdit(row)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                        </Box>
                    </Grid>
                </Grid>

                <Grid item xs={6}>
                    <Autocomplete
                        id="idwarehouse"
                        size="small"
                        value={selectedWarehouse || null}
                        options={store || []}
                        getOptionLabel={(option) => option?.name || ''}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        renderInput={(params) =>
                            <TextField {...params} label="Bodega" size="small" placeholder="(Todas las bodegas)" />
                        }
                        onChange={(e, newValue: any) => {
                            setSelectedWareHouse(newValue)
                        }}
                        disabled={statusStore === ApiStatus.FETCHING}
                        loading={statusStore === ApiStatus.FETCHING}
                    />
                </Grid>

                <Grid item xs={12}>
                    <CustomTable
                        columns={columns}
                        loading={statusStock === ApiStatus.FETCHING}
                        rows={stockproducts || []}
                        onRowClick={() => { }}
                        hasOptions

                        totalRows={totalRows}
                        rowsPerPage={perPage}
                        page={page}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                        dense
                        withoutTitle
                        withCustomPagination
                    />
                </Grid>
            </Grid>
            {showModal && (
                <AdjustmentModal
                    open={showModal}
                    onClose={() => setShowModal(false)}
                    stockStore={stockStore}
                    selectedProduct={product}
                    onList={getDataAPI}
                    getProductsApi={getProductsApi}
                />
            )}
        </Drawer>
    )
}